import { useApp } from "@/context/AppContext";
import { HOUR_HEIGHT, PX_PER_MIN, minutesToLabel, dateToStr, todayStr, eventsForDate, nowMinutes } from "@/lib/timeUtils";
import { useEffect, useState, useMemo } from "react";

export function WeekView({ cursor, onSelectDay, onEditEvent }) {
  const { events, settings, eventColor } = useApp();
  const format = settings?.time_format || "12";
  const weekStart = settings?.week_start || "mon";
  const dayStartHour = settings?.day_start_hour ?? 6;
  const dayEndHour = settings?.day_end_hour ?? 24;
  const dayStartMin = dayStartHour * 60;
  const totalHeight = (dayEndHour - dayStartHour) * HOUR_HEIGHT;
  const [liveMin, setLiveMin] = useState(nowMinutes());
  useEffect(() => { const t = setInterval(() => setLiveMin(nowMinutes()), 30000); return () => clearInterval(t); }, []);

  let offset = cursor.getDay();
  if (weekStart === "mon") offset = (offset + 6) % 7;
  const start = new Date(cursor); start.setDate(cursor.getDate() - offset);
  const days = Array.from({ length: 7 }).map((_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  const today = todayStr();
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const d of days) {
      const ds = dateToStr(d);
      map[ds] = eventsForDate(events, ds);
    }
    return map;
  }, [events, weekStart, start.getTime()]);
  const hours = []; for (let h = dayStartHour; h < dayEndHour; h++) hours.push(h);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* headers */}
      <div className="flex border-b pb-2 mb-1" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="w-[52px] shrink-0" />
        {days.map((d) => {
          const ds = dateToStr(d);
          const isToday = ds === today;
          return (
            <button key={ds} onClick={() => onSelectDay(ds)}
              className="flex-1 flex flex-col items-center py-1 rounded-xl transition-colors hover:opacity-80" data-testid={`week-day-header-${ds}`}>
              <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()]}</span>
              <span className="text-lg font-bold tnum w-9 h-9 flex items-center justify-center rounded-full mt-0.5"
                style={isToday ? { background: "var(--accent-primary)", color: "#fff" } : { color: "var(--text-primary)" }}>{d.getDate()}</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto hide-scrollbar">
        <div className="flex relative" style={{ height: totalHeight }}>
          {/* time rail */}
          <div className="w-[52px] shrink-0 relative">
            {hours.map((h, i) => (
              <span key={h} className="absolute right-2 text-[10px] font-mono tnum" style={{ top: i * HOUR_HEIGHT - 6, color: "var(--text-muted)" }}>
                {minutesToLabel(h * 60, format).replace(":00", "")}
              </span>
            ))}
          </div>
          {days.map((d) => {
            const ds = dateToStr(d);
            const dayEvents = eventsByDay[ds] || [];
            const isToday = ds === today;
            return (
              <div key={ds} className="flex-1 relative border-l" style={{ borderColor: "var(--border-subtle)" }}>
                {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-t" style={{ top: i * HOUR_HEIGHT, borderColor: "var(--border-subtle)", opacity: 0.5 }} />)}
                {dayEvents.map((ev) => {
                  const top = (ev.start_minutes - dayStartMin) * PX_PER_MIN;
                  const height = Math.max(16, (ev.end_minutes - ev.start_minutes) * PX_PER_MIN);
                  const color = eventColor(ev);
                  return (
                    <button key={ev.id} onClick={() => onEditEvent(ev)}
                      className="absolute rounded-md overflow-hidden text-left px-1.5 py-0.5 transition-transform hover:scale-[1.02] hover:z-20"
                      style={{ top, height, left: 2, right: 2, background: `color-mix(in srgb, ${color} 26%, var(--bg-card))`, borderLeft: `2px solid ${color}` }}
                      data-testid="week-event-block">
                      <span className="text-[10px] font-semibold truncate block leading-tight" style={{ color: "var(--text-primary)" }}>{ev.title}</span>
                      {height > 28 && <span className="text-[9px] font-mono block" style={{ color: "var(--text-secondary)" }}>{minutesToLabel(ev.start_minutes, format)}</span>}
                    </button>
                  );
                })}
                {isToday && (
                  <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: (liveMin - dayStartMin) * PX_PER_MIN }}>
                    <div className="h-[1.5px] w-full" style={{ background: "var(--timeline-indicator)" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
